'use client';
import { useState, type ReactNode, type Ref } from 'react';
import { Trace, TraceCollapsible, TraceCollapsibleContent, TraceCollapsibleTrigger, TraceIcon, TraceLine } from '../../components/ui/shadcn/trace';
import { useTranslation } from '../../lib/i18n';
import { type ToolItem } from '../../lib/transcript';
import { Button } from '../../components/ui/Button';
import { CodeBlock, CodeBlockContent, CodeBlockFooter, CodeBlockHeader, CodeBlockTitle } from '../../components/ui/shadcn/code-block';

export function ToolOutputBlock({ output }: { output: NonNullable<ToolItem['output']> }) {
  const { t } = useTranslation();
  // A truncated output offers the full text as a real toggle, not a broken promise of a terminal that
  // was never wired up — flip between the preview and the full body in place.
  const [expanded, setExpanded] = useState(false);
  const hasFull = !!output.fullText && output.fullText !== output.text;
  const text = [
    output.status,
    (expanded && output.fullText ? output.fullText : output.text) || ' ',
    ...(output.notes ?? []).map(note => `↳ ${note}`),
  ].filter(part => part !== undefined).join('\n');
  return (
    <CodeBlock size="sm" variant={output.tone === 'danger' ? 'destructive' : 'default'} data-testid="chat-tool-output" className="my-1">
      {output.command || output.cwd ? <CodeBlockHeader className="flex-wrap">
        {output.command ? <CodeBlockTitle className="whitespace-pre-wrap">$ {output.command}</CodeBlockTitle> : null}
        {output.cwd ? <span className="text-meta">({t.brainChat.eventCwd}: {output.cwd})</span> : null}
      </CodeBlockHeader> : null}
      <CodeBlockContent wrap className="max-h-64">{text}</CodeBlockContent>
      {hasFull ? (
        <CodeBlockFooter><Button
          variant="ghost"
          size="sm"
          data-testid="chat-tool-output-expand"
          className="mt-1"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t.brainChat.toolOutputCollapse : t.brainChat.toolOutputExpand}
        </Button></CodeBlockFooter>
      ) : null}
    </CodeBlock>
  );
}
/** The monospace log column the transcript's machine rows share: the assistant's tool calls and the chat
 *  event rows both stack in it, flush (no gap between rows), on one left edge indented (pl-4) deeper than
 *  the prose and at one type size, so a run of tool rows followed by event rows reads as one list. It is log output, so
 *  it is one of the few places the mono face is allowed, and the blocks a row unfolds inherit it. */
export function LogColumn({ children }: { children: ReactNode }) {
  return <Trace className="chat-log-column">{children}</Trace>;
}

/** The icon column of a log row: a fixed-width slot exactly one line tall, so a tool's text glyph, the
 *  running spinner, an event's lucide icon and the fold chevron all centre on the first line and every
 *  label starts at the same x. */
export function LogGlyph({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <TraceIcon className={className}>{children}</TraceIcon>;
}

/** A log row that folds: the native `<details>` disclosure (keyboard-operable, its expanded state exposed
 *  by the browser) with the row as its summary and a trailing chevron. `body` is what opening reveals;
 *  a row may instead unfold in place by styling its own text with the `group-open/fold` variant. */
export function LogFold({ head, body, lineRef, ...attrs }: {
  head: ReactNode;
  body?: ReactNode;
  /** The summary line, for a caller that measures it. */
  lineRef?: Ref<HTMLElement>;
  'data-testid'?: string;
  'data-tool-id'?: string;
}) {
  return (
    <TraceCollapsible {...attrs} className="group/fold">
      <TraceCollapsibleTrigger aria-controls={body ? undefined : ''}>
        <TraceLine ref={lineRef}>{head}</TraceLine>
      </TraceCollapsibleTrigger>
      {body ? <TraceCollapsibleContent>{body}</TraceCollapsibleContent> : null}
    </TraceCollapsible>
  );
}
